import clsx from 'clsx';
import { Eye, EyeOff, ExternalLink, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { useCreateTask, useSetTasksVisible, useStudioProject, useStudioTasks, useStudioTeam, useUpdateNow, useUpdateTask } from '@/api/studio';
import type { Project, ProjectHealth, Task } from '@/api/types';
import { PersonLink } from '@/components/PersonDialog';
import { Dialog, Empty, Status, TaskGlyph } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { useStudioTerms } from '@/lib/contour';
import { formatDue, formatMoney, formatWhen } from '@/lib/format';
import { toast } from '@/lib/toast';
import { health, taskStatus } from '@/lib/status';
import { StudioChanges, StudioOnboarding } from './ProjectExtras';
import { StudioProjectAccess } from './ProjectAccess';
import { StudioMeetings } from './StudioMeetings';
import { StudioReports } from './StudioReports';
import { ProjectSettings } from './ProjectSettings';
import { ProjectActs } from './ClientActs';
import { StudioFiles } from './StudioFiles';
import { RequestAccess } from './RequestAccess';
import { StudioThreads } from './StudioInbox';
import { NeedsEditor, StudioTask } from './StudioTask';

function NowEditor({ p }: { p: Project }) {
  const update = useUpdateNow();
  const [now, setNow] = useState(p.now);
  const [h, setH] = useState<ProjectHealth>(p.health);
  useEffect(() => {
    setNow(p.now);
    setH(p.health);
  }, [p.now, p.health]);
  const dirty = now !== p.now || h !== p.health;

  return (
    <section className="frame px-6 py-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Что клиент видит в «Сейчас»</h2>
        <span className="text-xs text-muted">обновлено {formatWhen(p.updatedAt)}</span>
      </div>
      <textarea className="field mt-3 min-h-24 text-md" value={now} onChange={(e) => setNow(e.target.value)} aria-label="Положение дел" />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(health) as ProjectHealth[]).map((k) => (
            <button key={k} className="chip" aria-pressed={h === k} onClick={() => setH(k)}>
              {health[k].label}
            </button>
          ))}
        </div>
        <button className="btn-primary" disabled={!dirty || update.isPending} onClick={() => update.mutate({ projectId: p.id, now: now.trim(), health: h })}>
          Опубликовать клиенту
        </button>
      </div>
      <p className="mt-3 text-xs text-muted">Одна-две фразы о том, где проект сегодня и что дальше. Обновляйте хотя бы раз в неделю — иначе проект попадёт во «Внимание».</p>
    </section>
  );
}

function NewTaskDialog({ p, open, onClose }: { p: Project; open: boolean; onClose: () => void }) {
  const { data: team } = useStudioTeam();
  const create = useCreateTask();
  const groups = p.kind === 'fixed' ? p.milestones.map((m) => m.title) : ['Кампании', 'Аналитика', 'Отчёты'];
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [group, setGroup] = useState(groups[0]);
  const [assigneeId, setAssigneeId] = useState(p.owner.id);
  const [due, setDue] = useState('');
  const [visible, setVisible] = useState(true);
  return (
    <Dialog open={open} onClose={onClose} title="Новая задача" wide>
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          create.mutate({ projectId: p.id, title: title.trim(), description, group, assigneeId, due: due || undefined, clientVisible: visible }, {
            onSuccess: (t) => {
              t.warnings?.forEach(toast);
              onClose();
            },
          });
        }}
      >
        <p className="rounded bg-panel px-3 py-2 text-sm text-muted shadow-hair">
          Задача создаётся в Linear{p.linear ? `, проект «${p.linear.name}»` : ''}, и работа по ней идёт там. Кабинет показывает её клиенту, если включено «Видит клиент».
        </p>
        <input className="field text-md" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Название, понятное клиенту" autoFocus />
        <textarea className="field min-h-24" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Что делаем и какой будет результат" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <select className="field" value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Этап">
            {groups.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
          <select className="field" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} aria-label="Исполнитель">
            {team?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <input className="field" type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Срок" />
        </div>
        <div className="flex items-center justify-between border-t border-line pt-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} /> Видит клиент
          </label>
          <button className="btn-primary" disabled={!title.trim() || create.isPending}>
            Создать в Linear
          </button>
        </div>
      </form>
    </Dialog>
  );
}

type TaskMode = 'visible' | 'all';
const MODE_KEY = 'portal.studio.tasks.mode';
function readMode(): TaskMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'all' ? 'all' : 'visible';
  } catch {
    return 'visible';
  }
}

function Tasks({ p }: { p: Project }) {
  const [mode, setMode] = useState<TaskMode>(readMode);
  const visibleQ = useStudioTasks(p.id, { visible: true });
  // The whole of Linear is fetched only when asked for.
  const allQ = useStudioTasks(p.id, { enabled: mode === 'all' });
  const tasksQ = mode === 'all' ? allQ : visibleQ;
  const tasks = tasksQ.data;
  const counts = { visible: (allQ.data ?? visibleQ.data)?.filter((t) => t.clientVisible).length, all: allQ.data?.length };
  const pick = (m: TaskMode) => {
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* private mode: the choice lasts until the page is closed */
    }
  };
  const update = useUpdateTask();
  const [needs, setNeeds] = useState<Task>();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const list = tasks?.filter((t) => showDone || t.status !== 'done') ?? [];
  const [sel, setSel] = useState<string[]>([]);
  const setVisible = useSetTasksVisible();
  const shown = list.map((t) => t.id);
  const allOn = shown.length > 0 && shown.every((id) => sel.includes(id));
  const flip = (id: string) => setSel((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  return (
    <>
      <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Какие задачи показывать">
        {(
          [
            ['visible', 'Видны клиенту'],
            ['all', 'Все из Linear'],
          ] as const
        ).map(([m, label]) => (
          <button key={m} className="chip" aria-pressed={mode === m} onClick={() => pick(m)}>
            {label}
            <span className="num opacity-60">{counts[m] ?? ''}</span>
          </button>
        ))}
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-muted">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Показывать готовые
        </label>
        <div className="flex gap-2">
          {p.linear && (
            <a className="btn-secondary" href={p.linear.url} target="_blank" rel="noreferrer">
              Открыть в Linear <ExternalLink className="size-4" />
            </a>
          )}
          <button className="btn-primary" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> Задача
          </button>
        </div>
      </div>
      {sel.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded bg-panel px-3 py-2 text-sm shadow-hair" role="region" aria-label="Выбранные задачи">
          <span className="mr-2">Выбрано: {sel.length}</span>
          <button className="btn-secondary" disabled={setVisible.isPending} onClick={() => setVisible.mutate({ projectId: p.id, ids: sel, visible: true }, { onSuccess: () => setSel([]) })}>
            Показать клиенту
          </button>
          <button className="btn-secondary" disabled={setVisible.isPending} onClick={() => setVisible.mutate({ projectId: p.id, ids: sel, visible: false }, { onSuccess: () => setSel([]) })}>
            Скрыть
          </button>
          <button className="btn-ghost" onClick={() => setSel([])}>
            Снять выбор
          </button>
          {setVisible.error && <span role="alert" className="text-overdue">{(setVisible.error as Error).message}</span>}
        </div>
      )}
      {!tasks ? (
        <QueryPending query={tasksQ} compact />
      ) : mode === 'visible' && tasks.length === 0 ? (
        <Empty title="Клиент пока не видит ни одной задачи">
          Поставьте в Linear метку portal_for_client или откройте «Все из Linear» и включите видимость.
        </Empty>
      ) : (
        <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-muted">
            <tr className="border-b border-line">
              <th className="w-6 py-2 pr-2 font-normal">
                <input type="checkbox" aria-label="Выбрать все задачи" checked={allOn} onChange={() => setSel(allOn ? [] : shown)} />
              </th>
              <th className="py-2 pr-3 font-normal">Задача</th>
              <th className="py-2 pr-3 font-normal">Статус в Linear</th>
              <th className="py-2 pr-3 font-normal">Ждём клиента</th>
              <th className="py-2 pr-3 font-normal">Срок</th>
              <th className="py-2 text-right font-normal">Видит клиент</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {list.map((t) => (
              <tr
                key={t.id}
                className={clsx('cursor-pointer hover:bg-panel', !t.clientVisible && 'text-muted')}
                // The whole row opens the task; its own controls (checkbox, links, buttons) keep working.
                onClick={(e) => {
                  if (!(e.target as HTMLElement).closest('a, button, input, label')) navigate(`/studio/projects/${p.id}/tasks/${t.id}`);
                }}
              >
                <td className="w-6 py-2 pr-2">
                  <input type="checkbox" aria-label={`Выбрать ${t.key || t.title}`} checked={sel.includes(t.id)} onChange={() => flip(t.id)} />
                </td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2.5">
                    <TaskGlyph status={t.status} waiting={!!t.needsClient && t.status !== 'done'} />
                    <span className="font-mono text-xs text-faint">{t.key}</span>
                    <Link to={`/studio/projects/${p.id}/tasks/${t.id}`} className="min-w-0 truncate hover:underline">
                      {t.title}
                    </Link>
                    {t.linearUrl && (
                      <a href={t.linearUrl} target="_blank" rel="noreferrer" className="text-faint hover:text-ink" aria-label="Открыть в Linear">
                        <ExternalLink className="size-3.5" />
                      </a>
                    )}
                  </div>
                </td>
                <td className="py-2 pr-3">{taskStatus[t.status].label}</td>
                <td className="py-2 pr-3">
                  <button className={clsx('rounded px-1.5 py-0.5 text-left hover:bg-sunken', t.needsClient ? 'text-waiting' : 'text-faint')} onClick={() => setNeeds(t)} disabled={t.status === 'done'}>
                    {t.needsClient ? t.needsClient.note : t.status === 'done' ? '—' : 'попросить…'}
                  </button>
                </td>
                <td className="py-2 pr-3 text-muted">{t.due ? formatDue(t.due) : '—'}</td>
                <td className="py-2 text-right">
                  <button
                    className={clsx('btn-ghost h-7 px-2', t.clientVisible ? 'text-ink' : 'text-faint')}
                    onClick={() => update.mutate({ id: t.id, patch: { clientVisible: !t.clientVisible } })}
                    aria-pressed={t.clientVisible}
                    // The Linear label wins over the toggle, so it is locked on.
                    disabled={t.visibleByLabel}
                    title={t.visibleByLabel ? 'Видна клиенту по метке portal_for_client в Linear' : undefined}
                    aria-label={t.visibleByLabel ? 'Видна клиенту по метке portal_for_client' : t.clientVisible ? 'Скрыть от клиента' : 'Показать клиенту'}
                  >
                    {t.clientVisible ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {needs && <NeedsEditor t={needs} onClose={() => setNeeds(undefined)} />}
      <NewTaskDialog p={p} open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

export function StudioProject() {
  // `/tasks/:threadId` is a task, `/talk/:threadId` a thread: the second segment depends on the tab.
  const { projectId = '', tab = 'status', threadId } = useParams();
  const projectQ = useStudioProject(projectId);
  const p = projectQ.data;
  const { acts: withActs } = useStudioTerms();
  if (!p) return <QueryPending query={projectQ} />;
  const h = health[p.health];
  const tabs = [
    ['status', 'Статус'],
    ['start', 'Запуск'],
    ['access', 'Доступы'],
    ['tasks', 'Задачи'],
    ['talk', 'Переписка'],
    ['reports', 'Отчёты'],
    ['meetings', 'Встречи'],
    ['changes', 'Идеи и запросы'],
    ['files', 'Файлы'],
    ...(withActs ? [['acts', 'Акты']] : []),
    ['settings', 'Настройки'],
  ];

  return (
    <>
      <header className="mb-6">
        <Link to={`/studio/clients/${p.clientId}`} className="text-sm text-muted hover:text-ink">
          ← Клиент
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <h1 className="text-xl font-semibold tracking-tight">{p.title}</h1>
          <Status tone={h.tone}>{h.label}</Status>
          <span className="ml-auto">
            <RequestAccess projectId={p.id} />
          </span>
        </div>
        <p className="mt-1 text-sm text-muted">
          {p.kind === 'fixed' ? 'Фиксированная услуга' : 'Регулярные платежи'}, {formatMoney(p.terms.price)}
          {p.kind === 'retainer' ? ' в месяц' : ''}, ведёт <PersonLink person={p.owner} className="rounded text-ink hover:underline hover:underline-offset-2" />
        </p>
      </header>
      <nav className="mb-6 flex gap-6 overflow-x-auto border-b border-line" aria-label="Разделы проекта">
        {tabs.map(([k, l]) => (
          <NavLink
            key={k}
            to={`/studio/projects/${p.id}${k === 'status' ? '' : `/${k}`}`}
            end
            className={() => clsx('-mb-px shrink-0 whitespace-nowrap border-b-2 pb-2.5 text-sm', tab === k ? 'border-ink font-medium' : 'border-transparent text-muted hover:text-ink')}
          >
            {l}
          </NavLink>
        ))}
      </nav>
      {tab === 'status' && <NowEditor p={p} />}
      {tab === 'start' && <StudioOnboarding p={p} />}
      {tab === 'access' && <StudioProjectAccess p={p} />}
      {tab === 'tasks' && (threadId ? <StudioTask key={threadId} p={p} taskId={threadId} /> : <Tasks p={p} />)}
      {tab === 'reports' && <StudioReports p={p} />}
      {tab === 'meetings' && <StudioMeetings p={p} />}
      {tab === 'changes' && <StudioChanges p={p} />}
      {tab === 'files' && <StudioFiles clientId={p.clientId} projectId={p.id} />}
      {tab === 'acts' && withActs && <ProjectActs project={p} />}
      {tab === 'settings' && <ProjectSettings p={p} />}
      {tab === 'talk' && <StudioThreads projectId={p.id} selectedId={threadId ?? `t-${p.id}`} base={`/studio/projects/${p.id}/talk`} />}
    </>
  );
}
